/**
 * useDragMove — pointer-based drag-to-move with grid snap.
 *
 * Core-flows §3 / glance-spec: pointer events on the drag handle (⠿ top-left).
 * On drop: compute target grid cell → resolveDrop (collision) → commitMove.
 * On invalid drop (collision, null result): spring-revert to origin (§3.3).
 *
 * State machine:
 *   IDLE → DRAGGING (onDragStart)
 *   DRAGGING → IDLE (valid drop → onCommit → IDLE)
 *   DRAGGING → REVERTING (invalid drop / cancel)
 *   REVERTING → IDLE (transitionend or timeout fallback; or instantly under reduced-motion)
 *
 * Immutability: all state transitions replace state wholesale.
 */
import { useState, useCallback, useRef } from 'react';
import type { Board } from 'briefpane/schema';
import { resolveDrop, pointerToGridX, pointerToGridY } from './layout-engine.js';
import { scheduleDragStart, cancelDragStart } from './use-long-press.js';

/** Phase discriminant for the drag state machine. */
export type DragPhase = 'idle' | 'dragging' | 'reverting';

export interface DragMoveState {
  /** Current phase of the drag state machine. */
  readonly phase: DragPhase;
  /** Derived convenience: true when phase is 'dragging'. */
  readonly isDragging: boolean;
  readonly dragTileId: string | null;
  /** Ghost grid position during drag (display only). */
  readonly ghostX: number;
  readonly ghostY: number;
  /**
   * During 'reverting': pixel position (relative to grid container) where the
   * ghost should animate FROM (the last pointer position when drop was released).
   * null in idle / dragging phases.
   */
  readonly revertFromX: number | null;
  readonly revertFromY: number | null;
  /**
   * During 'reverting': pixel position the ghost should animate TO — the centre
   * of the origin grid cell.
   */
  readonly revertToX: number | null;
  readonly revertToY: number | null;
}

export interface DragMoveHandlers {
  readonly state: DragMoveState;
  /** Call from the drag handle's onPointerDown. */
  readonly onDragStart: (tileId: string, e: React.PointerEvent | React.KeyboardEvent) => void;
  /** Attach to the grid container to capture pointer moves and end. */
  readonly onPointerMove: (e: React.PointerEvent) => void;
  readonly onPointerUp: (e: React.PointerEvent) => void;
  readonly onPointerCancel: () => void;
  /** Called by the ghost's onTransitionEnd to finalize the revert animation. */
  readonly onRevertEnd: () => void;
}

const IDLE: DragMoveState = {
  phase: 'idle',
  isDragging: false,
  dragTileId: null,
  ghostX: 0,
  ghostY: 0,
  revertFromX: null,
  revertFromY: null,
  revertToX: null,
  revertToY: null,
};

/** Returns true if the user prefers reduced motion. Safe to call outside browser. */
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// ---------------------------------------------------------------------------
// Pure state-builder helpers — exported for unit testing.
// The hook delegates to these; callers should never use them directly.
// ---------------------------------------------------------------------------

/**
 * Build the REVERTING state for an invalid drop.
 *
 * @param tileId - the tile being reverted
 * @param fromPx - pixel X where pointer was at drop time (relative to grid)
 * @param fromPy - pixel Y where pointer was at drop time
 * @param originX - grid column of the origin cell
 * @param originY - grid row of the origin cell
 * @param cellWidth - grid cell width in px (including gap)
 * @param cellHeight - grid cell height in px (including gap)
 */
export function buildRevertingState(
  tileId: string,
  fromPx: number,
  fromPy: number,
  originX: number,
  originY: number,
  cellWidth: number,
  cellHeight: number,
): DragMoveState {
  return {
    phase: 'reverting',
    isDragging: false,
    dragTileId: tileId,
    ghostX: originX,
    ghostY: originY,
    revertFromX: fromPx,
    revertFromY: fromPy,
    revertToX: originX * cellWidth + cellWidth / 2,
    revertToY: originY * cellHeight + cellHeight / 2,
  };
}

/** The canonical IDLE state — exported for tests that need to assert it. */
export const DRAG_IDLE_STATE: DragMoveState = {
  phase: 'idle',
  isDragging: false,
  dragTileId: null,
  ghostX: 0,
  ghostY: 0,
  revertFromX: null,
  revertFromY: null,
  revertToX: null,
  revertToY: null,
};

/**
 * useDragMove
 *
 * @param board - current board (for collision resolution)
 * @param gridContainerRef - ref to the grid container DOM element
 * @param cellWidth - grid cell width in px (including gap)
 * @param cellHeight - grid cell height in px (including gap)
 * @param onCommit - called with (tileId, x, y) when a drag ends successfully
 */
export function useDragMove(
  board: Board,
  gridContainerRef: React.RefObject<HTMLDivElement | null>,
  cellWidth: number,
  cellHeight: number,
  onCommit: (tileId: string, x: number, y: number) => void,
): DragMoveHandlers {
  const [state, setState] = useState<DragMoveState>(IDLE);
  // Track the original grid position so we can revert on invalid drop.
  const originRef = useRef<{ x: number; y: number } | null>(null);
  // Revert timeout fallback: cleared by transitionend, fires after --dur-dismiss (320ms).
  const revertTimerRef = useRef<number>(-1);
  // Long-press timer ID (touch-only). -1 = no pending timer.
  const longPressTimerRef = useRef<number>(-1);

  const onDragStart = useCallback(
    (tileId: string, e: React.PointerEvent | React.KeyboardEvent) => {
      if (!('pointerId' in e)) return; // keyboard activate goes through useKeyboardNav
      const entry = board.layout.find((l) => l.tileId === tileId);
      if (!entry) return;

      // For touch pointers: schedule drag start after TOUCH_LONG_PRESS_MS.
      // For mouse/pen: start immediately (delay = 0, synchronous callback).
      const domEvent = e as React.PointerEvent;
      longPressTimerRef.current = scheduleDragStart(domEvent.pointerType, () => {
        (domEvent.target as HTMLElement).setPointerCapture(domEvent.pointerId);
        originRef.current = { x: entry.x, y: entry.y };
        setState({
          phase: 'dragging',
          isDragging: true,
          dragTileId: tileId,
          ghostX: entry.x,
          ghostY: entry.y,
          revertFromX: null,
          revertFromY: null,
          revertToX: null,
          revertToY: null,
        });
      });
    },
    [board.layout],
  );

  const pointerToCell = useCallback(
    (e: React.PointerEvent): { x: number; y: number } => {
      const container = gridContainerRef.current;
      if (!container) return { x: 0, y: 0 };
      const rect = container.getBoundingClientRect();
      const relX = e.clientX - rect.left;
      const relY = e.clientY - rect.top;
      return {
        x: pointerToGridX(relX, cellWidth, board.grid.cols),
        y: pointerToGridY(relY, cellHeight),
      };
    },
    [board.grid.cols, cellHeight, cellWidth, gridContainerRef],
  );

  /** Compute pointer-relative-to-grid pixel position (for revert animation). */
  const pointerToPixels = useCallback(
    (e: React.PointerEvent): { px: number; py: number } => {
      const container = gridContainerRef.current;
      if (!container) return { px: 0, py: 0 };
      const rect = container.getBoundingClientRect();
      return { px: e.clientX - rect.left, py: e.clientY - rect.top };
    },
    [gridContainerRef],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (state.phase !== 'dragging' || !state.dragTileId) return;
      const { x, y } = pointerToCell(e);
      setState((prev) => ({ ...prev, ghostX: x, ghostY: y }));
    },
    [state.phase, state.dragTileId, pointerToCell],
  );

  /** Transition to IDLE and clear the revert timeout. */
  const clearToIdle = useCallback(() => {
    if (revertTimerRef.current !== -1) {
      clearTimeout(revertTimerRef.current);
      revertTimerRef.current = -1;
    }
    originRef.current = null;
    setState(IDLE);
  }, []);

  /**
   * Enter the REVERTING phase: animate the ghost from its current pointer pixel
   * position back to the origin cell's centre.
   *
   * prefers-reduced-motion: skip revert animation entirely — go straight to IDLE.
   */
  const enterRevert = useCallback(
    (tileId: string, fromPx: number, fromPy: number) => {
      const origin = originRef.current;
      // If reduced-motion or no origin recorded: instant dismiss.
      if (prefersReducedMotion() || !origin) {
        clearToIdle();
        return;
      }

      // Compute the centre of the origin cell in pixels relative to grid container.
      // The origin cell's top-left is (origin.x * cellWidth, origin.y * cellHeight);
      // we look up the tile footprint for an exact centring, but the ghost is
      // centred on the pointer so the pointer-to-centre distance is ~0 at drag
      // start — a cell-centre approximation is close enough here.
      const toX = origin.x * cellWidth + cellWidth / 2;
      const toY = origin.y * cellHeight + cellHeight / 2;

      setState({
        phase: 'reverting',
        isDragging: false,
        dragTileId: tileId,
        ghostX: origin.x,
        ghostY: origin.y,
        revertFromX: fromPx,
        revertFromY: fromPy,
        revertToX: toX,
        revertToY: toY,
      });

      // Fallback timeout: --dur-dismiss is 320ms; we allow 400ms for transitionend.
      revertTimerRef.current = window.setTimeout(() => {
        revertTimerRef.current = -1;
        originRef.current = null;
        setState(IDLE);
      }, 400);
    },
    [cellWidth, cellHeight, clearToIdle],
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent) => {
      // Cancel any pending long-press timer on pointer-up (touch lifted before hold).
      cancelDragStart(longPressTimerRef.current);
      longPressTimerRef.current = -1;
      if (state.phase !== 'dragging' || !state.dragTileId) return;
      const { x, y } = pointerToCell(e);
      const resolved = resolveDrop(board, state.dragTileId, x, y);

      if (resolved !== null) {
        originRef.current = null;
        setState(IDLE);
        onCommit(state.dragTileId, resolved.x, resolved.y);
        return;
      }

      // Invalid drop (null): spring-revert the ghost to origin (§3.3).
      // Displaced-neighbor previews clear as isDragging becomes false.
      const { px, py } = pointerToPixels(e);
      enterRevert(state.dragTileId, px, py);
    },
    [board, onCommit, pointerToCell, pointerToPixels, enterRevert, state.phase, state.dragTileId],
  );

  const onPointerCancel = useCallback(() => {
    // Cancel any pending long-press timer (touch interrupted before drag started).
    cancelDragStart(longPressTimerRef.current);
    longPressTimerRef.current = -1;
    // Pointer captured then cancelled (e.g. touch interrupted): revert quietly.
    clearToIdle();
  }, [clearToIdle]);

  /** Called by DragGhost's transitionend to cleanly exit the reverting phase. */
  const onRevertEnd = useCallback(() => {
    if (state.phase !== 'reverting') return;
    clearToIdle();
  }, [state.phase, clearToIdle]);

  return { state, onDragStart, onPointerMove, onPointerUp, onPointerCancel, onRevertEnd };
}
